import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";

export const Route = createFileRoute("/dev/typography")({
  component: TypographyPreview,
  head: () => ({
    meta: [
      { title: "Mobile Typography Preview · Coffman Cove Lodging" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

// Common mobile widths (CSS px). Includes the smallest still-supported (iPhone SE)
// up through the largest current iPhone Pro Max / Android phablets.
const WIDTHS = [320, 360, 375, 390, 414, 430] as const;
type Width = (typeof WIDTHS)[number];

function TypographyPreview() {
  const [showRulers, setShowRulers] = useState(true);
  const [showProse, setShowProse] = useState(true);
  const [highlightLH, setHighlightLH] = useState(false);
  const search = typeof window !== "undefined" ? window.location.search : "";
  const isSample = useMemo(() => new URLSearchParams(search).get("sample") === "1", [search]);

  if (isSample) {
    return <Sample highlightLH={highlightLH} showProse={showProse} />;
  }

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <header className="sticky top-0 z-20 border-b border-border bg-background/90 backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 py-4 flex flex-wrap items-center gap-3 justify-between">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--color-ember)] font-semibold">
              Dev Tool · not linked from nav
            </p>
            <h1 className="font-display text-xl sm:text-2xl truncate">
              Mobile Typography Preview
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Toggle label="Rulers" value={showRulers} onChange={setShowRulers} />
            <Toggle label="Prose sample" value={showProse} onChange={setShowProse} />
            <Toggle label="Line-height bands" value={highlightLH} onChange={setHighlightLH} />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6">
        <p className="text-sm text-muted-foreground max-w-prose">
          Each frame renders the same typographic sample at a fixed CSS width. Compare
          font-size, line-height, and <code>max-w-prose</code> wrapping side-by-side.
          The measurements panel under each frame reports actual computed values from
          the rendered DOM.
        </p>

        <div className="mt-6 grid gap-6 lg:grid-cols-2 xl:grid-cols-3">
          {WIDTHS.map((w) => (
            <Frame
              key={w}
              width={w}
              showRulers={showRulers}
              showProse={showProse}
              highlightLH={highlightLH}
            />
          ))}
        </div>
      </main>
    </div>
  );
}

function Toggle({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      aria-pressed={value}
      className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2 min-h-9 text-xs font-semibold uppercase tracking-wider transition ${
        value
          ? "border-[var(--color-ember)] bg-[var(--color-ember)] text-[var(--color-ember-foreground)]"
          : "border-border bg-background text-muted-foreground hover:text-foreground"
      }`}
    >
      <span
        aria-hidden
        className={`h-1.5 w-1.5 rounded-full ${
          value ? "bg-[var(--color-ember-foreground)]" : "bg-muted-foreground/60"
        }`}
      />
      {label}
    </button>
  );
}

function Frame({
  width,
  showRulers,
  showProse,
  highlightLH,
}: {
  width: Width;
  showRulers: boolean;
  showProse: boolean;
  highlightLH: boolean;
}) {
  const src = `/dev/typography?sample=1&prose=${showProse ? 1 : 0}&lh=${highlightLH ? 1 : 0}`;
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeH, setIframeH] = useState(900);
  const [measurements, setMeasurements] = useState<Measurement[] | null>(null);

  useEffect(() => {
    const f = iframeRef.current;
    if (!f) return;
    let raf = 0;
    const measure = () => {
      const doc = f.contentDocument;
      if (!doc?.body) return;
      const h = doc.documentElement.scrollHeight;
      setIframeH(Math.max(600, h));
      setMeasurements(readMeasurements(doc));
    };
    const onLoad = () => {
      measure();
      // re-measure after fonts settle
      raf = window.setTimeout(measure, 250) as unknown as number;
    };
    f.addEventListener("load", onLoad);
    return () => {
      f.removeEventListener("load", onLoad);
      window.clearTimeout(raf);
    };
  }, [src]);

  return (
    <section
      className="rounded-xl border border-border bg-card overflow-hidden shadow-sm"
      aria-label={`${width} pixel preview`}
    >
      <header className="flex items-center justify-between gap-2 border-b border-border bg-background/60 px-3 py-2">
        <p className="text-xs font-mono text-muted-foreground">
          <span className="text-foreground font-semibold">{width}px</span>{" "}
          <span className="opacity-60">· {labelFor(width)}</span>
        </p>
        <a
          href={src}
          target="_blank"
          rel="noreferrer"
          className="text-[10px] uppercase tracking-wider text-[var(--color-ember)] hover:underline"
        >
          Open sample
        </a>
      </header>

      <div className="bg-muted/40 p-3 overflow-x-auto">
        <div className="relative mx-auto" style={{ width }}>
          {showRulers && <Ruler width={width} />}
          <iframe
            ref={iframeRef}
            title={`Typography sample at ${width}px`}
            src={src}
            style={{ width, height: iframeH }}
            className="block bg-background border border-border rounded-md"
          />
        </div>
      </div>

      {measurements && (
        <ul className="grid grid-cols-2 gap-x-4 gap-y-1 border-t border-border px-3 py-2 text-[11px] font-mono text-muted-foreground">
          {measurements.map((m) => (
            <li key={m.label} className="flex justify-between gap-2">
              <span className="text-foreground/80">{m.label}</span>
              <span>
                {m.fontSize}/{m.lineHeight}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Ruler({ width }: { width: number }) {
  const ticks = Array.from({ length: Math.ceil(width / 16) + 1 }, (_, i) => i * 16);
  return (
    <div
      aria-hidden
      className="relative h-4 mb-1 text-[8px] font-mono text-muted-foreground"
      style={{ width }}
    >
      {ticks.map((t) => (
        <span
          key={t}
          className="absolute top-0 border-l border-border h-2"
          style={{ left: t }}
        >
          {t % 64 === 0 && <span className="absolute -top-0 left-0.5">{t}</span>}
        </span>
      ))}
    </div>
  );
}

interface Measurement {
  label: string;
  fontSize: string;
  lineHeight: string;
}

function readMeasurements(doc: Document): Measurement[] {
  const targets: { label: string; sel: string }[] = [
    { label: "h1", sel: "[data-typo='h1']" },
    { label: "h2", sel: "[data-typo='h2']" },
    { label: "h3", sel: "[data-typo='h3']" },
    { label: "lead", sel: "[data-typo='lead']" },
    { label: "body", sel: "[data-typo='body']" },
    { label: "small", sel: "[data-typo='small']" },
    { label: "label", sel: "[data-typo='label']" },
  ];
  const out: Measurement[] = [];
  for (const t of targets) {
    const el = doc.querySelector(t.sel) as HTMLElement | null;
    if (!el) continue;
    const cs = doc.defaultView?.getComputedStyle(el);
    if (!cs) continue;
    out.push({
      label: t.label,
      fontSize: `${Math.round(parseFloat(cs.fontSize))}px`,
      lineHeight:
        cs.lineHeight === "normal"
          ? "norm"
          : `${Math.round(parseFloat(cs.lineHeight))}px`,
    });
  }
  return out;
}

function labelFor(w: number) {
  switch (w) {
    case 320:
      return "iPhone SE / smallest";
    case 360:
      return "Android baseline";
    case 375:
      return "iPhone 13 mini / SE 3";
    case 390:
      return "iPhone 15";
    case 414:
      return "iPhone 11 / Plus";
    case 430:
      return "iPhone 15 Pro Max";
    default:
      return "";
  }
}

// ----- Sample content rendered inside each iframe -----
function Sample({ highlightLH, showProse }: { highlightLH: boolean; showProse: boolean }) {
  // Honor query overrides so the iframe is self-contained.
  const params = useMemo(
    () => new URLSearchParams(typeof window !== "undefined" ? window.location.search : ""),
    [],
  );
  const lh = params.get("lh") === "1" || highlightLH;
  const prose = params.get("prose") !== "0" && showProse;
  const bandStyle = lh
    ? {
        backgroundImage:
          "repeating-linear-gradient(to bottom, color-mix(in oklab, var(--color-ember) 12%, transparent) 0 1px, transparent 1px 1.5rem)",
      }
    : undefined;

  return (
    <div className="min-h-dvh bg-background text-foreground" style={bandStyle}>
      <div className="px-4 py-5 space-y-6">
        <header className="space-y-2">
          <p
            data-typo="label"
            className="text-[10px] uppercase tracking-[0.25em] text-[var(--color-ember)] font-semibold"
          >
            Fishing Report · Coffman Cove
          </p>
          <h1
            data-typo="h1"
            className="font-display text-3xl sm:text-4xl leading-tight text-balance"
          >
            Where we suggest you should fish today.
          </h1>
          <p data-typo="lead" className="text-base sm:text-lg text-muted-foreground max-w-prose">
            A real-time look at tides, barometer, wind, and moon phase — ranked across
            spots within a 10-mile radius of the dock.
          </p>
        </header>

        <section className="space-y-3">
          <h2 data-typo="h2" className="font-display text-2xl leading-snug">
            Today's top window
          </h2>
          <h3 data-typo="h3" className="font-display text-lg leading-snug">
            High slack at 6:42 AM, falling barometer
          </h3>
          <p data-typo="body" className={`text-sm ${prose ? "max-w-prose" : ""}`}>
            King Salmon feeding activity peaks during the first two hours of an outgoing
            tide when the barometric pressure is trending downward. Pair that with a
            waxing moon and you have the conditions our guests pull anchor for. This
            paragraph is intentionally long so you can verify wrapping behavior across
            screen widths — the line length should remain comfortable to read without
            running edge to edge on a phone.
          </p>
          <p data-typo="small" className="text-xs text-muted-foreground">
            Source: NOAA Tides & Currents station 9450906 · Open-Meteo marine forecast ·
            updated every 10 minutes.
          </p>
        </section>

        <section className="space-y-2">
          <h2 data-typo="h2" className="font-display text-2xl leading-snug">
            Numeric scale
          </h2>
          <div className="grid grid-cols-2 gap-3 text-foreground">
            <Metric label="Pressure" value="29.94" unit="inHg" />
            <Metric label="Wind" value="12" unit="kn" />
            <Metric label="High tide" value="14.2" unit="ft" />
            <Metric label="Water temp" value="52" unit="°F" />
          </div>
        </section>

        {prose && (
          <section className="space-y-3">
            <h2 data-typo="h2" className="font-display text-2xl leading-snug">
              Prose wrapping (max-w-prose)
            </h2>
            <article className="prose-like max-w-prose space-y-3 text-sm leading-relaxed">
              <p>
                More than 40 years ago, Jerry and Caroline Hedges built what was simply
                known as the bunkhouse — a place where friends could gather to experience
                everything they loved about Alaska: incredible fishing, breathtaking
                scenery, and the kind of hospitality that turns strangers into lifelong
                friends.
              </p>
              <p>
                Many of our guests return year after year. Some have been making the trip
                for more than four decades, becoming part of our extended family and
                creating memories that span generations.
              </p>
            </article>
          </section>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, unit }: { label: string; value: string; unit: string }) {
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2">
      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
      <p className="font-display text-2xl leading-none">
        {value}
        <span className="text-sm ml-1 text-muted-foreground">{unit}</span>
      </p>
    </div>
  );
}
